import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useAccess } from "../context/AccessContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Heart, MessageCircle, UserPlus, Crown, Check, Send, MessageSquare, CornerDownLeft, Flame, Search, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import TierBadge from "../components/TierBadge";
import { trackEvent, EVENTS } from "../lib/analytics";
import useUnreadCount from "../hooks/useUnreadCount";
import { adminDeletePost, adminDeleteComment } from "../lib/adminApi";

export default function Friends() {
  const { user } = useAuth();
  const { effectivePremium } = useAccess();
  const navigate = useNavigate();

  if (!effectivePremium) {
    return (
      <div className="grid place-items-center py-20" data-testid="community-paywall">
        <div className="max-w-md rounded-3xl border border-amber-500/30 bg-gradient-to-b from-amber-500/10 to-transparent p-8 text-center">
          <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-amber-500 text-white"><Crown size={22}/></div>
          <h2 className="mt-4 font-heading text-2xl font-semibold">Community is Premium</h2>
          <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-400">Connect with fellow aspirants, share doubts, like and comment — unlock the Syllox community.</p>
          <p className="mt-2 text-xs font-medium text-amber-600 dark:text-amber-400" data-testid="community-paywall-pricing">₹49/month · ₹299/year</p>
          <Button onClick={() => navigate("/premium")} className="mt-6 bg-gradient-to-r from-amber-400 to-orange-500 text-white" data-testid="upsell-premium-btn">
            Upgrade to Premium
          </Button>
        </div>
      </div>
    );
  }

  return <CommunityInner />;
}

const CommunityInner = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { effectivePremium } = useAccess();
  // Reuses the same hook the sidebar previously drove — same fetch interval,
  // same data, zero new network calls or polling loops.
  const unread = useUnreadCount(effectivePremium);
  const [tab, setTab] = useState("feed");
  const [posts, setPosts] = useState([]);
  const [newPost, setNewPost] = useState("");
  const [friends, setFriends] = useState([]);
  const [requests, setRequests] = useState([]);
  const [openComments, setOpenComments] = useState({});
  const [feedCursor, setFeedCursor] = useState(null);
  const [feedHasMore, setFeedHasMore] = useState(true);
  const [feedLoading, setFeedLoading] = useState(false);
  const isAdmin = !!user?.is_admin;

  const removePostAsAdmin = async (postId) => {
    if (!window.confirm("Delete this post and all its comments? This cannot be undone.")) return;
    try {
      await adminDeletePost(postId);
      setPosts((prev) => prev.filter((p) => p.post_id !== postId));
      toast.success("Post removed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't delete post");
    }
  };

  useEffect(() => { trackEvent(EVENTS.COMMUNITY_OPENED); }, []);

  const loadAll = useCallback(async () => {
    // Use allSettled so a transient failure on one endpoint (e.g. /friends/requests
    // hiccup) doesn't blank the entire feed render.
    const [f, r, p] = await Promise.allSettled([
      api.get("/friends"),
      api.get("/friends/requests"),
      api.get("/community/feed?limit=20"),
    ]);
    if (f.status === "fulfilled") setFriends(f.value.data);
    if (r.status === "fulfilled") setRequests(r.value.data);
    if (p.status === "fulfilled") {
      setPosts(p.value.data.items || []);
      setFeedCursor(p.value.data.next_cursor);
      setFeedHasMore(!!p.value.data.next_cursor);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const loadMorePosts = async () => {
    if (!feedCursor || feedLoading) return;
    setFeedLoading(true);
    try {
      const { data } = await api.get(`/community/feed?limit=20&cursor=${encodeURIComponent(feedCursor)}`);
      setPosts((prev) => [...prev, ...(data.items || [])]);
      setFeedCursor(data.next_cursor);
      setFeedHasMore(!!data.next_cursor);
    } finally { setFeedLoading(false); }
  };

  const post = async () => {
    if (!newPost.trim()) return;
    try {
      const { data } = await api.post("/community/posts", { content: newPost.trim() });
      // Optimistically prepend so the user sees their post immediately —
      // matches the pattern used by like() and avoids a brittle full refetch.
      setPosts((prev) => [data, ...prev]);
      setNewPost("");
      toast.success("Posted!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't post");
    }
  };

  const like = async (post_id) => {
    const { data } = await api.post(`/community/posts/${post_id}/like`);
    setPosts((p) => p.map((x) => x.post_id === post_id ? { ...x, likes_count: data.likes_count, liked_by_me: data.liked_by_me } : x));
  };

  const toggleComments = (post_id) => {
    setOpenComments((o) => ({ ...o, [post_id]: !o[post_id] }));
  };

  const bumpCommentCount = (post_id, delta) => {
    setPosts((p) => p.map((x) => x.post_id === post_id
      ? { ...x, comments_count: Math.max((x.comments_count || 0) + delta, 0) }
      : x
    ));
  };

  const accept = async (id) => {
    await api.post(`/friends/accept/${id}`);
    trackEvent(EVENTS.FRIEND_ADDED, { method: "accept_request" });
    toast.success("Friend added!");
    loadAll();
  };

  // Callback for the FindFriendSearch child — when it successfully sends a
  // request, we refresh the friend list so the friend appears immediately.
  const onSearchRequestSent = useCallback(() => { loadAll(); }, [loadAll]);

  return (
    <div className="space-y-6" data-testid="community-root">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-emerald-600">Community</div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Aspirants, together.</h1>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="feed" data-testid="tab-feed">Feed</TabsTrigger>
          <TabsTrigger value="friends" data-testid="tab-friends">Friends</TabsTrigger>
          <TabsTrigger value="requests" data-testid="tab-requests">Requests {requests.length > 0 && <span className="ml-1 rounded-full bg-emerald-500 px-1.5 text-xs text-white">{requests.length}</span>}</TabsTrigger>
          {/* Messages tab — navigates to /messages on click instead of toggling
              an inline tab pane. Keeps the full chat UX (which lives on its
              own route) discoverable from inside Community per Jun 2026 spec
              ("Messages should only exist inside Community → Messages"). */}
          <TabsTrigger
            value="messages-link"
            data-testid="tab-messages"
            onClick={(e) => { e.preventDefault(); navigate("/messages"); }}
          >
            Messages
            {unread > 0 && (
              <span
                className="ml-1.5 rounded-full bg-emerald-500 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-white"
                data-testid="tab-messages-unread"
                aria-label={`${unread} unread`}
              >
                {unread > 99 ? "99+" : unread}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="feed" className="mt-4 space-y-4">
          <div className="rounded-2xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-900">
            <Textarea placeholder="Share a doubt, tip, or breakthrough…" value={newPost} onChange={(e)=>setNewPost(e.target.value)} data-testid="new-post-input"/>
            <div className="mt-2 flex justify-end">
              <Button onClick={post} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="post-btn"><Send size={14} className="mr-1"/> Post</Button>
            </div>
          </div>
          {posts.length === 0 ? (
            <div className="rounded-2xl border border-dashed p-10 text-center text-sm text-zinc-500">Be the first to post something.</div>
          ) : posts.map((p) => (
            <div key={p.post_id} className="rounded-2xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-900" data-testid={`post-${p.post_id}`}>
              <div className="flex items-center gap-3">
                {p.author?.picture ? (
                  <img src={p.author.picture} className="h-9 w-9 rounded-full object-cover"/>
                ) : (
                  <div className="grid h-9 w-9 place-items-center rounded-full bg-emerald-500 text-sm font-semibold text-white">{p.author?.name?.[0] || "?"}</div>
                )}
                <div>
                  <div className="flex items-center gap-1.5 text-sm font-medium">
                    <span>{p.author?.name || "Deleted User"}</span>
                    <TierBadge tier={p.author?.tier} size="xs" />
                  </div>
                  <div className="text-xs text-zinc-500">@{p.author?.username || "deleted"} · {new Date(p.created_at).toLocaleString()}</div>
                </div>
              </div>
              <p className="mt-3 whitespace-pre-wrap text-sm">{p.content}</p>
              <div className="mt-3 flex items-center gap-4 text-sm text-zinc-500">
                <button onClick={() => like(p.post_id)} className={`flex items-center gap-1 hover:text-red-500 ${p.liked_by_me ? "text-red-500" : ""}`} data-testid={`like-btn-${p.post_id}`}>
                  <Heart size={16} fill={p.liked_by_me ? "currentColor" : "none"}/> {p.likes_count}
                </button>
                <button
                  onClick={() => toggleComments(p.post_id)}
                  className={`flex items-center gap-1 hover:text-emerald-600 ${openComments[p.post_id] ? "text-emerald-600" : ""}`}
                  data-testid={`comments-toggle-${p.post_id}`}
                >
                  <MessageCircle size={16}/> {p.comments_count}
                </button>
                {isAdmin && (
                  <button
                    onClick={() => removePostAsAdmin(p.post_id)}
                    className="ml-auto flex items-center gap-1 text-red-500 hover:text-red-600"
                    data-testid={`admin-delete-post-${p.post_id}`}
                    title="Remove post (admin)"
                  >
                    <Trash2 size={14}/> Remove
                  </button>
                )}
              </div>
              {openComments[p.post_id] && (
                <CommentsSection
                  postId={p.post_id}
                  isAdmin={isAdmin}
                  onAdded={() => bumpCommentCount(p.post_id, 1)}
                  onCommentRemoved={() => bumpCommentCount(p.post_id, -1)}
                />
              )}
            </div>
          ))}
          {feedHasMore && posts.length > 0 && (
            <div className="pt-2 text-center">
              <Button variant="ghost" size="sm" onClick={loadMorePosts} disabled={feedLoading} data-testid="feed-load-more">
                {feedLoading ? "Loading…" : "Show more posts"}
              </Button>
            </div>
          )}
        </TabsContent>

        <TabsContent value="friends" className="mt-4 space-y-4">
          <FindFriendSearch onRequestSent={onSearchRequestSent} />
          {friends.length === 0 ? (
            <div className="rounded-2xl border border-dashed p-10 text-center text-sm text-zinc-500">No friends yet. Send a request above or browse suggestions below.</div>
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {friends.map((f) => (
                <div key={f.user_id} className="flex items-center gap-3 rounded-xl border border-zinc-200 bg-white p-3 dark:border-zinc-800 dark:bg-zinc-900" data-testid={`friend-card-${f.user_id}`}>
                  {f.picture ? <img src={f.picture} className="h-10 w-10 rounded-full object-cover" alt={f.name}/> : <div className="grid h-10 w-10 place-items-center rounded-full bg-emerald-500 text-sm font-semibold text-white">{f.name?.[0]}</div>}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <button onClick={() => navigate(`/u/${f.username}`)} className="block truncate text-left text-sm font-medium hover:text-emerald-600" data-testid={`friend-view-${f.user_id}`}>{f.name}</button>
                      <TierBadge tier={f.tier} size="xs" />
                    </div>
                    <div className="truncate text-xs text-zinc-500">@{f.username}</div>
                  </div>
                  <Button size="sm" onClick={() => navigate(`/messages/${f.user_id}`)} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid={`message-friend-${f.user_id}`}>
                    <MessageSquare size={14} className="mr-1"/> Message
                  </Button>
                </div>
              ))}
            </div>
          )}

          <SuggestionsSection />
        </TabsContent>

        <TabsContent value="requests" className="mt-4 space-y-3">
          {requests.length === 0 ? (
            <div className="rounded-2xl border border-dashed p-10 text-center text-sm text-zinc-500">No pending requests.</div>
          ) : requests.map((r) => (
            <div key={r.friendship_id} className="flex items-center justify-between rounded-xl border border-zinc-200 bg-white p-3 dark:border-zinc-800 dark:bg-zinc-900">
              <div className="flex items-center gap-3">
                {r.from_user?.picture ? <img src={r.from_user.picture} className="h-10 w-10 rounded-full object-cover"/> : <div className="grid h-10 w-10 place-items-center rounded-full bg-emerald-500 text-sm font-semibold text-white">{r.from_user?.name?.[0]}</div>}
                <div>
                  <div className="flex items-center gap-1.5 text-sm font-medium">
                    <span>{r.from_user?.name}</span>
                    <TierBadge tier={r.from_user?.tier} size="xs" />
                  </div>
                  <div className="text-xs text-zinc-500">@{r.from_user?.username}</div>
                </div>
              </div>
              <Button onClick={() => accept(r.friendship_id)} size="sm" className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid={`accept-${r.friendship_id}`}><Check size={14} className="mr-1"/> Accept</Button>
            </div>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
};

function CommentsSection({ postId, isAdmin, onAdded, onCommentRemoved }) {
  const { user } = useAuth();
  const [comments, setComments] = useState(null); // null = loading
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/community/posts/${postId}/comments`);
      setComments(data);
    } catch {
      setComments([]);
    }
  }, [postId]);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    const content = text.trim();
    if (!content || posting) return;
    setPosting(true);
    try {
      const { data } = await api.post("/community/comments", { post_id: postId, content });
      // Backend returns the author; fall back to current user if missing
      if (!data.author && user) {
        data.author = { name: user.name, username: user.username, picture: user.picture };
      }
      setComments((c) => [...(c || []), data]);
      setText("");
      onAdded?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't post");
    } finally {
      setPosting(false);
    }
  };

  const removeCommentAsAdmin = async (commentId) => {
    if (!window.confirm("Delete this comment? This cannot be undone.")) return;
    try {
      await adminDeleteComment(commentId);
      setComments((c) => (c || []).filter((x) => x.comment_id !== commentId));
      onCommentRemoved?.();
      toast.success("Comment removed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't delete comment");
    }
  };

  return (
    <div
      className="mt-3 border-t border-zinc-100 pt-3 dark:border-zinc-800"
      data-testid={`comments-section-${postId}`}
    >
      {comments === null ? (
        <div className="py-2 text-xs text-zinc-400">Loading comments…</div>
      ) : comments.length === 0 ? (
        <div className="py-2 text-xs text-zinc-400">No comments yet. Be the first.</div>
      ) : (
        <div className="space-y-2.5">
          {comments.map((c) => (
            <div
              key={c.comment_id}
              className="flex items-start gap-2.5"
              data-testid={`comment-${c.comment_id}`}
            >
              {c.author?.picture ? (
                <img src={c.author.picture} className="h-7 w-7 shrink-0 rounded-full object-cover" alt={c.author?.name}/>
              ) : (
                <div className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-emerald-500 text-[11px] font-semibold text-white">
                  {c.author?.name?.[0]?.toUpperCase() || "?"}
                </div>
              )}
              <div className="flex-1 rounded-xl bg-zinc-50 px-3 py-2 dark:bg-zinc-800/60">
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                  <span className="text-xs font-semibold text-zinc-900 dark:text-zinc-100">
                    {c.author?.name || "Deleted User"}
                  </span>
                  <TierBadge tier={c.author?.tier} size="xs" />
                  <span className="text-[10px] text-zinc-400">
                    {new Date(c.created_at).toLocaleString()}
                  </span>
                  {isAdmin && (
                    <button
                      type="button"
                      onClick={() => removeCommentAsAdmin(c.comment_id)}
                      className="ml-auto flex items-center gap-1 text-[10px] text-red-500 hover:text-red-600"
                      data-testid={`admin-delete-comment-${c.comment_id}`}
                      title="Remove comment (admin)"
                    >
                      <Trash2 size={12}/> Remove
                    </button>
                  )}
                </div>
                <p className="mt-0.5 whitespace-pre-wrap text-sm text-zinc-700 dark:text-zinc-200">
                  {c.content}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      <form onSubmit={submit} className="mt-3 flex items-center gap-2">
        <Input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Write a comment…"
          maxLength={500}
          data-testid={`comment-input-${postId}`}
          className="h-9 text-sm"
        />
        <Button
          type="submit"
          size="sm"
          disabled={!text.trim() || posting}
          className="bg-emerald-500 hover:bg-emerald-600 text-white"
          data-testid={`comment-submit-${postId}`}
        >
          <CornerDownLeft size={14}/>
        </Button>
      </form>
    </div>
  );
}

function SuggestionsSection() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [pendingIds, setPendingIds] = useState(new Set());

  const loadMore = useCallback(async (resetCursor = null) => {
    if (loading) return;
    setLoading(true);
    try {
      const params = resetCursor ? `?limit=8` : `?limit=8${cursor ? `&cursor=${cursor}` : ""}`;
      const { data } = await api.get(`/friends/suggestions${params}`);
      setItems((prev) => (resetCursor === "reset" ? data.items : [...prev, ...data.items]));
      setCursor(data.next_cursor);
      setHasMore(!!data.next_cursor);
    } catch {
      // Silent — suggestions are non-critical
      setHasMore(false);
    } finally { setLoading(false); }
  }, [cursor, loading]);

  useEffect(() => { loadMore("reset"); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const sendRequest = async (u) => {
    setPendingIds((s) => new Set(s).add(u.user_id));
    try {
      await api.post(`/friends/request/by-id/${u.user_id}`);
      trackEvent(EVENTS.FRIEND_ADDED, { method: "suggestion" });
      toast.success(`Request sent to ${u.name || u.username}`);
      // Remove from suggestion list
      setItems((prev) => prev.filter((x) => x.user_id !== u.user_id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send");
    } finally {
      setPendingIds((s) => { const n = new Set(s); n.delete(u.user_id); return n; });
    }
  };

  if (!items.length && !loading) return null;

  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-900" data-testid="suggestions-section">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-heading text-base font-semibold">Suggested aspirants</h3>
          <p className="text-xs text-zinc-500">Build your study circle.</p>
        </div>
      </div>
      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
        {items.map((u) => (
          <div key={u.user_id} className="flex items-center gap-3 rounded-xl border border-zinc-200 p-3 dark:border-zinc-800" data-testid={`suggestion-${u.user_id}`}>
            {u.picture ? (
              <img src={u.picture} alt={u.name} className="h-10 w-10 rounded-full object-cover"/>
            ) : (
              <div className="grid h-10 w-10 place-items-center rounded-full bg-emerald-500 text-sm font-semibold text-white">{(u.name?.[0] || "?").toUpperCase()}</div>
            )}
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <button onClick={() => navigate(`/u/${u.username}`)} className="block max-w-full truncate text-left text-sm font-medium hover:text-emerald-600">{u.name || u.username}</button>
                <TierBadge tier={u.tier} size="xs" />
              </div>
              <div className="truncate text-xs text-zinc-500">@{u.username} · <Flame className="inline -mt-0.5 text-amber-500" size={10}/> {u.streak_days || 0}d</div>
              {u.bio && <div className="mt-0.5 truncate text-[11px] text-zinc-400">{u.bio}</div>}
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => sendRequest(u)}
              disabled={pendingIds.has(u.user_id)}
              data-testid={`suggestion-add-${u.user_id}`}
            >
              <UserPlus size={14} className="mr-1"/>{pendingIds.has(u.user_id) ? "…" : "Add"}
            </Button>
          </div>
        ))}
      </div>
      {hasMore && (
        <div className="mt-4 text-center">
          <Button variant="ghost" size="sm" onClick={() => loadMore()} disabled={loading} data-testid="suggestions-load-more">
            {loading ? "Loading…" : "Show more"}
          </Button>
        </div>
      )}
    </div>
  );
}


/**
 * Live search over ALL aspirants by username OR display name.
 *
 * • Debounced 300ms so we don't hit /api/friends/search on every keystroke.
 * • Case-insensitive, partial-contains match (implemented server-side).
 * • Ranked: exact-username > exact-name > partial-username > partial-name.
 * • Each result shows photo, display name, @username, tier badge, streak,
 *   and an Add Friend button that's hidden for existing/pending friendships.
 * • Empty result set: friendly "No users found" message.
 */
function FindFriendSearch({ onRequestSent }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pendingIds, setPendingIds] = useState(new Set());
  const [sentIds, setSentIds] = useState(new Set());
  // Track the query that produced the current `results` array so we can
  // ignore stale responses (e.g. slow "abc" landing AFTER quick "abcd").
  const activeQueryRef = useRef("");

  // Debounce: the previously-scheduled fetch is cancelled by the cleanup
  // whenever the query changes within 300ms. Keeps traffic minimal.
  useEffect(() => {
    const trimmed = q.trim();
    if (trimmed.length === 0) {
      setResults([]);
      setError(null);
      setLoading(false);
      activeQueryRef.current = "";
      return;
    }
    setLoading(true);
    setError(null);
    const t = setTimeout(async () => {
      activeQueryRef.current = trimmed;
      try {
        const { data } = await api.get(`/friends/search?q=${encodeURIComponent(trimmed)}`);
        // Discard if a newer query fired while this was in flight.
        if (activeQueryRef.current !== trimmed) return;
        setResults(data.items || []);
      } catch (e) {
        if (activeQueryRef.current !== trimmed) return;
        setError(e?.response?.data?.detail || "Search failed. Try again.");
        setResults([]);
      } finally {
        if (activeQueryRef.current === trimmed) setLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const addFriend = useCallback(async (user) => {
    if (pendingIds.has(user.user_id) || sentIds.has(user.user_id)) return;
    setPendingIds((s) => new Set(s).add(user.user_id));
    try {
      await api.post(`/friends/request/by-id/${user.user_id}`);
      trackEvent(EVENTS.FRIEND_ADDED, { method: "username_search" });
      toast.success(`Friend request sent to ${user.name || user.username}`);
      setSentIds((s) => new Set(s).add(user.user_id));
      onRequestSent?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send request");
    } finally {
      setPendingIds((s) => { const n = new Set(s); n.delete(user.user_id); return n; });
    }
  }, [pendingIds, sentIds, onRequestSent]);

  const showEmpty = useMemo(
    () => q.trim().length > 0 && !loading && results.length === 0 && !error,
    [q, loading, results, error]
  );

  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-900" data-testid="find-friend-search">
      <div className="relative">
        <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name or username"
          className="pl-9 pr-9"
          aria-label="Search friends by name or username"
          data-testid="friend-search-input"
          autoComplete="off"
          spellCheck={false}
        />
        {loading && (
          <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-zinc-400" data-testid="friend-search-loading" />
        )}
      </div>

      {error && (
        <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-300" data-testid="friend-search-error">
          {error}
        </div>
      )}

      {showEmpty && (
        <div className="mt-3 rounded-lg border border-dashed border-zinc-200 p-4 text-center text-sm text-zinc-500 dark:border-zinc-800" data-testid="friend-search-empty">
          No users found. Try searching by name or username.
        </div>
      )}

      {results.length > 0 && (
        <ul className="mt-3 divide-y divide-zinc-100 dark:divide-zinc-800" data-testid="friend-search-results">
          {results.map((u) => {
            const disabled = u.friendship_status === "accepted"
              || u.friendship_status === "pending"
              || sentIds.has(u.user_id);
            const label = u.friendship_status === "accepted"
              ? "Friends"
              : (u.friendship_status === "pending" || sentIds.has(u.user_id))
                ? "Sent"
                : pendingIds.has(u.user_id) ? "…" : "Add";
            return (
              <li key={u.user_id} className="flex items-center gap-3 py-2.5" data-testid={`friend-search-result-${u.user_id}`}>
                <button onClick={() => navigate(`/u/${u.username}`)} className="shrink-0" aria-label={`Open ${u.username}`}>
                  {u.picture
                    ? <img src={u.picture} alt={u.name} className="h-10 w-10 rounded-full object-cover"/>
                    : <div className="grid h-10 w-10 place-items-center rounded-full bg-emerald-500 text-sm font-semibold text-white">{(u.name?.[0] || u.username?.[0] || "?").toUpperCase()}</div>}
                </button>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => navigate(`/u/${u.username}`)}
                      className="truncate text-left text-sm font-medium hover:text-emerald-600"
                      data-testid={`friend-search-name-${u.user_id}`}
                    >
                      {u.name || u.username}
                    </button>
                    <TierBadge tier={u.tier} size="xs" />
                  </div>
                  <div className="flex items-center gap-2 truncate text-xs text-zinc-500">
                    <span>@{u.username}</span>
                    <span className="inline-flex items-center gap-0.5">
                      <Flame className="text-amber-500" size={10}/> {u.streak_days || 0}d
                    </span>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant={disabled ? "outline" : "default"}
                  onClick={() => addFriend(u)}
                  disabled={disabled || pendingIds.has(u.user_id)}
                  className={disabled ? "" : "bg-emerald-500 hover:bg-emerald-600 text-white"}
                  data-testid={`friend-search-add-${u.user_id}`}
                >
                  {!disabled && <UserPlus size={14} className="mr-1" />}
                  {label}
                </Button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
